/* eslint-disable react-hooks/exhaustive-deps */
/*
 * BaseStructuresTab — sister browser to the Tech Tree, listing every
 * buildable item the player can place in a base camp. Ports the bot's
 * /structure + /structurelist commands (see
 * bot/src/cogs/utility/lookup.py) to a fast in-page browser:
 *   • sticky search + category filter chips (10 categories)
 *   • sortable grid (name / build work / unlock level)
 *   • click a card → modal with description, materials, work cost
 *
 * Data: GET /api/base-structures and /api/base-structures/categories.
 */
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Search, Hammer, Package, X, Filter, Target, Wand2 } from "lucide-react";
import { resolveImg } from "@/lib/resolveImg";

function StructureCard({ s, onClick }) {
  const img = resolveImg(s.image);
  return (
    <button
      type="button"
      onClick={onClick}
      className="glass-card p-3 text-left hover:ring-1 hover:ring-[var(--teal)]/40 transition-transform hover:-translate-y-0.5 cursor-pointer flex items-center gap-3"
      data-testid={`structure-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-12 h-12 rounded bg-black/40 flex items-center justify-center shrink-0 ring-1 ring-white/10">
        {img ? (
          <img src={img} alt={s.name} className="w-10 h-10 object-contain" draggable={false} loading="lazy" />
        ) : (
          <Package size={20} className="text-[var(--muted)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--bone)] truncate">{s.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-2 mt-0.5">
          {s.level != null && (
            <span className="text-[var(--teal)]">Lv {s.level}</span>
          )}
          {s.build_work != null && (
            <span className="flex items-center gap-1">
              <Hammer size={10} /> {s.build_work.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function StructureModal({ s, color, onClose, onMaterialClick }) {
  const img = resolveImg(s.image);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="structure-modal"
    >
      <div
        className="glass-card relative max-w-xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)] p-1 rounded-full hover:bg-white/5 z-10" aria-label="Close" data-testid="structure-modal-close">
          <X size={18} />
        </button>
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4 mb-5">
            <div
              className="w-20 h-20 rounded-lg ring-1 flex items-center justify-center shrink-0"
              style={{ background: `${color}1a`, borderColor: `${color}55` }}
            >
              {img ? <img src={img} alt={s.name} className="w-16 h-16 object-contain" /> : <Package size={36} style={{ color }} />}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-semibold tracking-tight" data-testid="structure-modal-name">{s.name}</h2>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <span
                  className="text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full"
                  style={{ color, borderColor: `${color}55` }}
                >
                  {s.category}
                </span>
                {s.level != null && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--teal)] border border-[var(--teal-soft)] px-2 py-0.5 rounded-full">
                    Unlocks at Lv {s.level}
                  </span>
                )}
                {s.build_work != null && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-300/30 bg-amber-300/5 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Hammer size={10} /> {s.build_work.toLocaleString()} work
                  </span>
                )}
              </div>
            </div>
          </div>

          {s.description ? (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Description</div>
              <p className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap">{s.description}</p>
            </div>
          ) : (
            <p className="text-sm text-[var(--muted)] italic mb-5">No description on file for this structure.</p>
          )}

          {s.materials?.length > 0 ? (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 flex items-center gap-2">
                <Hammer size={11} /> Required Materials
              </div>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {s.materials.map((m, i) => {
                  const mimg = resolveImg(m.image);
                  return (
                    <li
                      key={i}
                      onClick={() => onMaterialClick?.(m.item)}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded bg-black/20 border border-white/5 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.04)] cursor-pointer transition-colors"
                      title={`See every tech that needs ${m.item}`}
                      data-testid={`structure-modal-mat-${i}`}
                    >
                      {mimg ? (
                        <img src={mimg} alt={m.item} className="w-8 h-8 rounded object-contain bg-black/30 ring-1 ring-white/10 shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-black/30 ring-1 ring-white/10 shrink-0" />
                      )}
                      <span className="text-sm text-white/85 flex-1 truncate">{m.item}</span>
                      <span className="font-mono text-[var(--teal)] text-sm">×{m.qty.toLocaleString()}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-[var(--muted)] mt-1.5 text-right">
                Tip: click any material to see every tech that uses it.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[var(--muted)] italic">No materials required.</p>
          )}

          {s.materials?.length > 0 && (
            <RouterLink
              to={`/planner?target=${encodeURIComponent(s.name)}&qty=1`}
              className="w-full btn btn-primary text-sm flex items-center justify-center gap-2 mt-4"
              data-testid="structure-modal-plan-craft"
              title={`Open the Crafting Planner with ${s.name} pre-selected`}
            >
              <Wand2 size={14} /> Plan this craft
            </RouterLink>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BaseStructuresTab({ onMaterialClick }) {
  const toast = useToast();
  const [structures, setStructures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [sort, setSort] = useState("name"); // name | work | level
  const [selected, setSelected] = useState(null);
  // Base Camp Planner — when set, only show structures whose unlock
  // level is ≤ this value. `null` = show everything. Capped at 65
  // (the Palworld level cap).
  const [maxLevel, setMaxLevel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/base-structures"),
      api.get("/base-structures/categories"),
    ])
      .then(([listRes, catRes]) => {
        if (cancelled) return;
        setStructures(listRes.data.structures || []);
        setCategories(catRes.data.categories || []);
      })
      .catch((e) => { if (!cancelled) { toast.error(apiError(e)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Map category name → its assigned colour, used both on chips and
  // inside the detail modal. Defaults to a neutral grey for unknown
  // categories.
  const colorByCat = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.name] = c.color;
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = structures;
    if (activeCat !== "All") rows = rows.filter((s) => s.category === activeCat);
    if (maxLevel != null) {
      // Items missing a known unlock level are treated as "available
      // anytime" so we don't accidentally hide every furniture piece
      // (most of which don't have a direct tech entry).
      rows = rows.filter((s) => s.level == null || s.level <= maxLevel);
    }
    if (needle) rows = rows.filter((s) =>
      s.name.toLowerCase().includes(needle)
      || (s.description || "").toLowerCase().includes(needle)
    );
    rows = [...rows];
    if (sort === "work") rows.sort((a, b) => (a.build_work || 0) - (b.build_work || 0) || a.name.localeCompare(b.name));
    else if (sort === "level") rows.sort((a, b) => (a.level ?? 999) - (b.level ?? 999) || a.name.localeCompare(b.name));
    else rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [structures, activeCat, q, sort, maxLevel]);

  return (
    <div className="space-y-3" data-testid="base-structures-root">
      <div className="glass-card p-3 flex flex-col sm:flex-row sm:items-center gap-2 sticky top-2 z-10 backdrop-blur">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search size={14} className="text-[var(--muted)] shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search structures (e.g. 'workbench', 'chest', 'wall')…"
            className="input flex-1"
            data-testid="structures-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="btn btn-ghost text-xs" data-testid="structures-search-clear">
              Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Filter size={12} className="text-[var(--muted)]" />
          <span className="text-[var(--muted)] mr-1">Sort:</span>
          {[
            ["name", "Name"],
            ["work", "Build Work"],
            ["level", "Unlock Lv"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSort(k)}
              className={`px-2 py-1 rounded text-[11px] uppercase tracking-wider transition-colors ${
                sort === k
                  ? "text-[var(--teal)] border border-[var(--teal-soft)] bg-[rgba(0,229,255,0.06)]"
                  : "text-[var(--muted)] border border-transparent hover:text-white"
              }`}
              data-testid={`structures-sort-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="glass-card p-2 flex flex-wrap gap-1.5" data-testid="structures-category-chips">
        <CatChip
          active={activeCat === "All"}
          onClick={() => setActiveCat("All")}
          color="#19D3E6"
          label="All"
          count={structures.length}
          testid="structures-cat-all"
        />
        {categories.map((c) => (
          <CatChip
            key={c.name}
            active={activeCat === c.name}
            onClick={() => setActiveCat(c.name)}
            color={c.color}
            label={c.name}
            count={c.count}
            testid={`structures-cat-${c.name.toLowerCase()}`}
          />
        ))}
      </div>

      <BaseCampPlanner
        maxLevel={maxLevel}
        onChange={setMaxLevel}
        structures={structures}
        filteredCount={filtered.length}
      />

      {loading && <Loader />}

      {!loading && filtered.length === 0 && (
        <div className="glass-card p-8 text-center text-[var(--muted)] text-sm" data-testid="structures-empty">
          No structures match these filters.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="structures-grid">
          {filtered.map((s) => (
            <StructureCard key={s.name} s={s} onClick={() => setSelected(s)} />
          ))}
        </div>
      )}

      {selected && (
        <StructureModal
          s={selected}
          color={colorByCat[selected.category] || "#19D3E6"}
          onClose={() => setSelected(null)}
          onMaterialClick={(name) => { setSelected(null); onMaterialClick?.(name); }}
        />
      )}
    </div>
  );
}

function CatChip({ active, onClick, color, label, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors flex items-center gap-1.5"
      style={
        active
          ? { color, borderColor: `${color}aa`, background: `${color}1a` }
          : { color: "var(--muted)", borderColor: "rgba(255,255,255,0.08)", background: "transparent" }
      }
    >
      <span className="font-semibold">{label}</span>
      <span className="opacity-70 font-mono normal-case tracking-normal">{count}</span>
    </button>
  );
}


/**
 * Base Camp Planner — drag the slider to a target character level and
 * the grid shrinks to "stuff I can actually build by then". A small
 * progress bar shows what fraction of the catalog is reachable. Off by
 * default to keep the page identical to its prior behaviour.
 */
function BaseCampPlanner({ maxLevel, onChange, structures, filteredCount }) {
  const total = structures.length;
  // How many structures unlock at-or-before each candidate level —
  // pre-computed once per structures change so dragging the slider
  // stays snappy.
  const reachableByLevel = useMemo(() => {
    const arr = new Array(66).fill(0);
    for (const s of structures) {
      const lv = s.level == null ? 1 : s.level;
      const start = Math.max(1, Math.min(65, lv));
      // Mark every level >= start as containing this structure.
      for (let i = start; i <= 65; i += 1) arr[i] += 1;
    }
    return arr;
  }, [structures]);

  const enabled = maxLevel != null;
  const lv = enabled ? maxLevel : 30;
  const reachable = reachableByLevel[Math.max(1, Math.min(65, lv))] || 0;
  const pct = total ? Math.round((reachable / total) * 100) : 0;

  return (
    <div
      className="glass-card p-3 sm:p-4 space-y-2"
      data-testid="base-camp-planner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="planner-toggle-label">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? lv : null)}
            className="accent-[var(--teal)]"
            data-testid="planner-toggle"
          />
          <Target size={14} className="text-[var(--teal)]" />
          <span className="font-semibold text-[var(--bone)]">Base Camp Planner</span>
          <span className="text-[var(--muted)] text-xs">— show only buildables unlocked by a target level</span>
        </label>
        {enabled && (
          <span className="text-xs font-mono text-[var(--teal)]" data-testid="planner-summary">
            {filteredCount} / {total} reachable · {pct}%
          </span>
        )}
      </div>
      {enabled && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] shrink-0">Lv 1</span>
          <input
            type="range"
            min={1}
            max={65}
            step={1}
            value={lv}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className="flex-1 accent-[var(--teal)]"
            aria-label="Target unlock level"
            data-testid="planner-slider"
          />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] shrink-0">Lv 65</span>
          <span
            className="font-mono text-sm text-[var(--teal)] tabular-nums w-16 text-right"
            data-testid="planner-current-level"
          >
            Lv {lv}
          </span>
        </div>
      )}
    </div>
  );
}
